import debounce from 'debounce-promise'
import React, { useMemo, useRef, useState } from 'react'

import ExploreReview from './ExploreReview'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import IconButton from 'components/Luxkit/Button/IconButton'
import Carousel from 'components/Luxkit/Carousel/Carousel'
import {
  ScrollPosition,
  getElementContentWidth,
  getHorizontalScrollPosition,
} from 'components/Luxkit/Carousel/utils'
import LineAngleLeftBIcon from 'components/Luxkit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'components/Luxkit/Icons/line/LineAngleRightBIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import { ExploreItem } from 'tripPlanner/types/common'

interface Props {
  item: ExploreItem
}

function ExploreReviews({ item }: Props) {
  const [position, setPosition] = useState<ScrollPosition>('start')
  const carouselRef = useRef<HTMLDivElement>(null)

  /** Based on logic from CardCarousel */
  const onCarouselArrowClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!carouselRef.current) {
      return
    }
    const direction = e.currentTarget.dataset.direction
    const contentWidth = getElementContentWidth(carouselRef.current)

    const nextLeft =
      direction === 'left' ?
        carouselRef.current.scrollLeft - contentWidth :
        carouselRef.current.scrollLeft + contentWidth

    carouselRef.current.scrollTo({
      left: nextLeft,
      behavior: 'smooth',
    })
  }

  const onScroll = useMemo(() => {
    return debounce(() => {
      if (carouselRef.current) {
        setPosition(getHorizontalScrollPosition(carouselRef.current))
      }
    }, 32)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const reviews = item.reviews ?? []
  const showCarousel = reviews.length > 1

  return (
    <VerticalSpacer gap={16}>
      <Group direction="horizontal" horizontalAlign="space-between">
        <VerticalSpacer gap={4}>
          {reviews.length > 0 && (
            <BodyText variant="medium" weight="bold">
              Reviews
            </BodyText>
          )}
          {!!item.rating && (
            <GooglePlaceRatingDisplay
              rating={item.rating}
              ratingsCount={item.userRatingCount}
              size="S"
            />
          )}
        </VerticalSpacer>
        {showCarousel && (
          <div>
            <IconButton
              kind="tertiary"
              size="medium"
              shape="square"
              onClick={onCarouselArrowClick}
              data-direction="left"
              disabled={position === 'start'}
            >
              <LineAngleLeftBIcon />
            </IconButton>
            <IconButton
              kind="tertiary"
              size="medium"
              shape="square"
              onClick={onCarouselArrowClick}
              data-direction="right"
              disabled={position === 'end'}
            >
              <LineAngleRightBIcon />
            </IconButton>
          </div>
        )}
      </Group>

      {reviews.length === 1 && <ExploreReview review={reviews[0]} />}

      {showCarousel && (
        <div>
          <Carousel
            snap="start"
            gutterSize={0}
            width="100%"
            ref={carouselRef}
            onScroll={onScroll}
          >
            {reviews.map((review) => (
              <div key={`${review.authorUrl}-${review.publishTime}`}>
                <ExploreReview review={review} />
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </VerticalSpacer>
  )
}

export default ExploreReviews
